/** LoopClosedBurst — 閉ループ完成時の祝福バーストエフェクト(Phase A2 Day 12 polish)。
 *  Reference: docs/CLAUDE_DESIGN_BRIEF.md §1.1 Painterly Joy
 *  - 領地化された範囲の重心から拡大する金色のリング + 内側の薄塗り
 *  - 0.6 秒で消滅、複数同時可
 */

import { Container, Graphics } from 'pixi.js';
import type { GridConfig, GridPos } from '../core/types';

interface Droplet {
  /** 飛ぶ方向の単位ベクトル */
  dx: number;
  dy: number;
  /** 最終的に飛ぶ距離(セル単位) */
  distanceCells: number;
  /** 半径(セル単位) */
  radiusCells: number;
  /** 開始からの遅延(秒) */
  delay: number;
}

interface BurstEntry {
  cx: number;
  cy: number;
  age: number;
  duration: number;
  droplets: Droplet[];
  /** 波紋最大半径のスケール倍率(intensity に応じて 1.0〜1.6) */
  maxRadiusScale: number;
}

const BURST_DURATION_SEC = 0.68;
const BURST_MAX_RADIUS_CELLS = 4.8;
const DROPLET_COUNT = 12;
const DROPLET_DURATION_SEC = 0.52;

export class LoopClosedBurst {
  public readonly view: Container;

  private readonly graphics: Graphics;
  private readonly grid: GridConfig;
  private readonly color: number;
  private active: BurstEntry[] = [];

  constructor(grid: GridConfig, color: number) {
    this.grid = grid;
    this.color = color;
    this.view = new Container();
    this.view.eventMode = 'none';
    this.graphics = new Graphics();
    this.view.addChild(this.graphics);
  }

  /** 領地化された cells の重心からバーストを発火。
   *  @param intensity 0..1 の強度スケール(省略時 = 0)。面積に比例して波紋半径・飛沫数を増幅。 */
  trigger(cells: readonly GridPos[], intensity = 0): void {
    if (cells.length === 0) return;
    let sx = 0;
    let sy = 0;
    for (const c of cells) {
      sx += c.x;
      sy += c.y;
    }
    sx /= cells.length;
    sy /= cells.length;

    // intensity に応じて飛沫数・距離を増幅(既存呼び出し intensity=0 で従来と同動作)
    const intensityScale = 1 + intensity; // 1.0〜2.0
    const dropletCount = Math.round(DROPLET_COUNT * intensityScale);
    const maxRadiusScale = 1 + intensity * 0.6; // 1.0〜1.6

    // ランダム角度で dropletCount 個の飛沫を配置(等角度ベース + ランダム揺らぎ)
    const droplets: Droplet[] = [];
    for (let i = 0; i < dropletCount; i++) {
      const base = (i / dropletCount) * Math.PI * 2;
      const jitter = (Math.random() - 0.5) * (Math.PI / dropletCount) * 0.8;
      const angle = base + jitter;
      droplets.push({
        dx: Math.cos(angle),
        dy: Math.sin(angle),
        distanceCells: (1.8 + Math.random() * 2.6) * intensityScale,
        radiusCells: 0.12 + Math.random() * 0.16,
        delay: Math.random() * 0.08,
      });
    }

    this.active.push({
      cx: (sx + 0.5) * this.grid.cellPx,
      cy: (sy + 0.5) * this.grid.cellPx,
      age: 0,
      duration: BURST_DURATION_SEC * (1 + intensity * 0.2),
      droplets,
      maxRadiusScale,
    });
  }

  /** Pixi ticker から毎フレーム呼ぶ。dt 秒経過で寿命管理 + 描画更新。 */
  update(dt: number): void {
    if (this.active.length === 0) {
      this.graphics.clear();
      return;
    }

    this.graphics.clear();
    const survivors: BurstEntry[] = [];
    const cp = this.grid.cellPx;
    for (const burst of this.active) {
      burst.age += dt;
      const t = burst.age / burst.duration;
      if (t >= 1) continue;

      // 1) リング(従来通り、若干太く + 二重に)
      const easeOut = 1 - (1 - t) * (1 - t);
      const radius = easeOut * cp * BURST_MAX_RADIUS_CELLS * burst.maxRadiusScale;
      const fade = 1 - t;
      const ringAlpha = Math.pow(fade, 1.18) * 0.52;
      const innerAlpha = Math.pow(fade, 1.6) * 0.08;
      const outerWidth = Math.max(2, cp * 0.1) * (1 - t * 0.34);
      const highlightWidth = Math.max(1, cp * 0.04) * (1 - t * 0.2);

      this.graphics.circle(burst.cx, burst.cy, radius);
      this.graphics.stroke({ width: outerWidth, color: this.color, alpha: ringAlpha });

      this.graphics.circle(burst.cx, burst.cy, radius * 0.7);
      this.graphics.stroke({ width: highlightWidth, color: 0xffffff, alpha: ringAlpha * 0.42 });

      this.graphics.circle(burst.cx, burst.cy, radius * 0.5);
      this.graphics.fill({ color: this.color, alpha: innerAlpha });

      // 2) 飛沫粒子(放射状にイーズアウトで飛び、後半でアルファフェード)
      for (const d of burst.droplets) {
        const dropAge = burst.age - d.delay;
        if (dropAge <= 0) continue;
        const dt2 = Math.min(1, dropAge / DROPLET_DURATION_SEC);
        const easeOutDrop = 1 - (1 - dt2) * (1 - dt2);
        const distPx = easeOutDrop * d.distanceCells * cp;
        const px = burst.cx + d.dx * distPx;
        const py = burst.cy + d.dy * distPx;
        const dropAlpha = Math.pow(1 - dt2, 1.35) * 0.78;
        const dropRadius = d.radiusCells * cp * (0.86 + dt2 * 0.24);
        this.graphics.circle(px - d.dx * dropRadius * 0.45, py - d.dy * dropRadius * 0.45, dropRadius * 0.52);
        this.graphics.fill({ color: this.color, alpha: dropAlpha * 0.36 });
        this.graphics.circle(px, py, dropRadius);
        this.graphics.fill({ color: this.color, alpha: dropAlpha });
        // 飛沫のハイライト
        this.graphics.circle(px - dropRadius * 0.32, py - dropRadius * 0.34, dropRadius * 0.34);
        this.graphics.fill({ color: 0xffffff, alpha: dropAlpha * 0.42 });
      }

      survivors.push(burst);
    }
    this.active = survivors;
  }

  clear(): void {
    this.active = [];
    this.graphics.clear();
  }
}
